import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  Divider, Alert, CircularProgress, MenuItem, TextField,
  Dialog, DialogContent, DialogTitle, IconButton, Tooltip,
  Collapse, List, ListItem
} from '@mui/material';
import {
  ArrowBack, PictureAsPdf, Visibility, Close, Edit,
  History, ExpandMore, ExpandLess, InfoOutlined, Business, Description
} from '@mui/icons-material';
import { Offer } from '../../types';
import { getOffer, updateOfferStatus, getOfferRevisions } from '../../api/offers';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { downloadOfferPdf, previewOfferPdf } from '../../api/pdf';

// ─── Sabitler ─────────────────────────────────────────────────────────────────
const BRAND  = '#1A3A6B';
const ACCENT = '#c0392b';
const BORDER = '#e8eaf0';

// ─── Status ───────────────────────────────────────────────────────────────────
const statusLabel: Record<string, string> = {
  DRAFT: 'Taslak', SENT: 'Gönderildi', ACCEPTED: 'Kabul', REJECTED: 'Red'
};
const statusStyle: Record<string, { bg: string; color: string }> = {
  DRAFT:    { bg: '#fff7ed', color: '#c2410c' },
  SENT:     { bg: '#eff6ff', color: '#1d4ed8' },
  ACCEPTED: { bg: '#f0fdf4', color: '#15803d' },
  REJECTED: { bg: '#fff0f0', color: '#dc2626' },
};
const StatusBadge: React.FC<{ status: string; size?: 'sm' | 'md' }> = ({ status, size = 'sm' }) => {
  const s = statusStyle[status] || { bg: '#f4f5f7', color: '#6b7280' };
  return (
      <Box component="span" sx={{
        display: 'inline-block', px: size === 'md' ? 1.25 : 1, py: size === 'md' ? '4px' : '3px',
        borderRadius: '20px', fontSize: size === 'md' ? '12px' : '11px', fontWeight: 700, lineHeight: 1.4,
        bgcolor: s.bg, color: s.color,
      }}>
        {statusLabel[status] || status}
      </Box>
  );
};

// ─── Tablo başlık hücresi ──────────────────────────────────────────────────────
const TH: React.FC<{ children?: React.ReactNode; align?: 'left'|'right'|'center'; width?: number|string }> = ({ children, align = 'left', width }) => (
    <TableCell align={align} style={{
      background: '#f8f9fc', color: '#8a92a6',
      fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em',
      borderBottom: `1px solid ${BORDER}`, paddingTop: 12, paddingBottom: 12, width,
    }}>
      {children}
    </TableCell>
);

// ─── InfoRow ──────────────────────────────────────────────────────────────────
const InfoRow: React.FC<{ label: string; value: string | undefined | null; bold?: boolean }> = ({ label, value, bold }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, alignItems: 'baseline', gap: 1 }}>
      <Typography sx={{ fontSize: '12px', color: '#8a92a6', flexShrink: 0 }}>{label}</Typography>
      <Typography sx={{ fontSize: '12px', fontWeight: bold ? 700 : 400, color: '#1a2535', textAlign: 'right' }}>{value || '—'}</Typography>
    </Box>
);

// ─── Outline button yardımcı ──────────────────────────────────────────────────
const outlineBtn = (color: string) => ({
  borderColor: color, color,
  borderRadius: '7px', textTransform: 'none' as const,
  fontSize: '12px', fontWeight: 600,
  '&:hover': { borderColor: color, bgcolor: color + '12' },
});

export interface OfferRevision {
  id: number; revisionNo: number; revisionLabel: string;
  changedByName: string; note: string; snapshotJson: string;
  currency: string; deliveryLocation: string; status: string;
  downPaymentRate: number; paymentDays: number; createdAt: string;
}

const OfferDetailPage: React.FC = () => {
  const { id }   = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const [offer, setOffer]             = useState<Offer | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState('');
  const [updating, setUpdating]       = useState(false);
  const [pdfUrl, setPdfUrl]           = useState<string | null>(null);
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [revisions, setRevisions]     = useState<OfferRevision[]>([]);
  const [revLoading, setRevLoading]   = useState(false);
  const [revPanelOpen, setRevPanelOpen] = useState(false);
  const [selectedRevision, setSelectedRevision] = useState<OfferRevision | null>(null);
  const [revModalOpen, setRevModalOpen] = useState(false);

  useEffect(() => {
    if (!id) return;
    getOffer(parseInt(id)).then(setOffer).catch(() => setError('Teklif yüklenemedi')).finally(() => setLoading(false));
  }, [id]);

  const loadRevisions = async () => {
    if (!id) return;
    setRevLoading(true);
    try { const data = await getOfferRevisions(parseInt(id)); setRevisions(data); setRevPanelOpen(true); }
    catch { setError('Revizyon geçmişi yüklenemedi'); }
    finally { setRevLoading(false); }
  };

  const toggleRevPanel = () => {
    if (!revPanelOpen && revisions.length === 0) loadRevisions();
    else setRevPanelOpen(prev => !prev);
  };

  const handleStatusChange = async (status: string) => {
    if (!offer) return;
    setUpdating(true);
    try { setOffer(await updateOfferStatus(offer.id, status)); }
    catch (e: any) { setError('Durum güncellenemedi: ' + (e.response?.data?.message || e.message)); }
    finally { setUpdating(false); }
  };

  const handlePreview = async () => {
    if (!offer) return;
    setPdfLoading(true);
    try { setPdfUrl(await previewOfferPdf(offer.id)); }
    catch { setError('PDF önizleme açılamadı'); }
    finally { setPdfLoading(false); }
  };

  const handleClosePdf = () => { if (pdfUrl) window.URL.revokeObjectURL(pdfUrl); setPdfUrl(null); };
  const parseSnapshot = (json: string) => { try { return JSON.parse(json); } catch { return null; } };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (!offer)  return <Alert severity="error">Teklif bulunamadı</Alert>;

  const itemsTotal  = offer.items?.reduce((sum, i) => sum + (i.totalPrice || 0), 0) || 0;
  const grandTotal  = itemsTotal + (offer.shippingAmount || 0);
  const currencySym = offer.currency === 'TRY' ? '₺' : offer.currency === 'EUR' ? '€' : '$';
  const isFL        = offer.orderNo?.includes('-FL-');

  return (
      <Box sx={{ p: 3, bgcolor: '#f5f6fa', minHeight: '100vh' }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Button startIcon={<ArrowBack sx={{ fontSize: 16 }} />} onClick={() => navigate('/offers')}
                    sx={{ color: '#8a92a6', textTransform: 'none', fontSize: '13px', borderRadius: '8px',
                      '&:hover': { bgcolor: '#f0f1f5', color: '#1a2535' } }}>
              Teklifler
            </Button>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                {isFL && <Chip label="FL" size="small" sx={{ bgcolor: '#7C3AED', color: '#fff', fontWeight: 700, fontSize: '11px', height: 20 }} />}
                <Typography sx={{ fontFamily: 'monospace', fontSize: 20, fontWeight: 700, color: BRAND, letterSpacing: 1 }}>
                  {offer.orderNo}
                </Typography>
                <StatusBadge status={offer.status} size="md" />
              </Box>
              <Typography sx={{ fontSize: '12px', color: '#8a92a6' }}>
                {offer.customerName} · {new Date(offer.createdAt).toLocaleDateString('tr-TR')} · {offer.createdByName}
              </Typography>
            </Box>
          </Box>

          {/* Aksiyon butonları */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {isAdmin && (
                <TextField select size="small" label="Durum" value={offer.status}
                           disabled={updating} onChange={e => handleStatusChange(e.target.value)}
                           sx={{ minWidth: 150, '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '13px' } }}>
                  <MenuItem value="DRAFT">Taslak</MenuItem>
                  <MenuItem value="SENT">Gönderildi</MenuItem>
                  <MenuItem value="ACCEPTED">Kabul Edildi</MenuItem>
                  <MenuItem value="REJECTED">Reddedildi</MenuItem>
                </TextField>
            )}
            <Button variant="outlined" size="small"
                    startIcon={revLoading ? <CircularProgress size={13} /> : <History sx={{ fontSize: 15 }} />}
                    onClick={toggleRevPanel} disabled={revLoading} sx={outlineBtn('#7C3AED')}
                    endIcon={revPanelOpen ? <ExpandLess sx={{ fontSize: 15 }} /> : <ExpandMore sx={{ fontSize: 15 }} />}>
              Geçmiş{revisions.length > 0 ? ` (${revisions.length})` : ''}
            </Button>
            <Button variant="outlined" size="small" startIcon={<Edit sx={{ fontSize: 15 }} />}
                    onClick={() => navigate(`/offers/${offer.id}/edit`)} sx={outlineBtn('#B45309')}>
              Düzenle
            </Button>
            {offer.status === 'ACCEPTED' && (
                <Button variant="outlined" size="small"
                        onClick={() => navigate(`/materials?offer=${offer.id}`)} sx={outlineBtn('#16A34A')}>
                  ⬡ Hammadde
                </Button>
            )}
            <Button variant="outlined" size="small"
                    startIcon={pdfLoading ? <CircularProgress size={13} /> : <Visibility sx={{ fontSize: 15 }} />}
                    onClick={handlePreview} disabled={pdfLoading}
                    sx={{ borderRadius: '7px', textTransform: 'none', fontSize: '12px', fontWeight: 600 }}>
              Önizle
            </Button>
            <Button variant="contained" size="small" startIcon={<PictureAsPdf sx={{ fontSize: 15 }} />}
                    onClick={() => downloadOfferPdf(offer.id, offer.orderNo)}
                    sx={{ bgcolor: ACCENT, borderRadius: '7px', textTransform: 'none', fontWeight: 600, boxShadow: 'none',
                      '&:hover': { bgcolor: '#a93226', boxShadow: 'none' } }}>
              PDF İndir
            </Button>
          </Box>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* ── Revizyon Paneli ── */}
        <Collapse in={revPanelOpen}>
          <Paper elevation={0} sx={{ mb: 3, border: '1px solid #e9d5ff', bgcolor: '#faf5ff', borderRadius: '10px', overflow: 'hidden' }}>
            <Box sx={{ px: 3, py: 1.75, display: 'flex', alignItems: 'center', gap: 1.25, borderBottom: '1px solid #e9d5ff', bgcolor: '#f5f0ff' }}>
              <History sx={{ color: '#7C3AED', fontSize: 16 }} />
              <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#7C3AED', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Revizyon Geçmişi
              </Typography>
              <Typography sx={{ fontSize: '11px', color: '#8a92a6' }}>
                — Her düzenlemede mevcut hali otomatik arşivlenir
              </Typography>
            </Box>
            {revisions.length === 0 ? (
                <Box sx={{ px: 3, py: 2.5 }}>
                  <Typography sx={{ fontSize: '13px', color: '#8a92a6' }}>Henüz revizyon kaydı yok.</Typography>
                </Box>
            ) : (
                <List disablePadding>
                  {revisions.map((rev, idx) => {
                    const snap = parseSnapshot(rev.snapshotJson);
                    return (
                        <ListItem key={rev.id} divider={idx < revisions.length - 1}
                                  sx={{ px: 3, py: 1.5, '&:hover': { bgcolor: '#f5f0ff' } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', flexWrap: 'wrap' }}>
                            <Chip label={rev.revisionLabel} size="small"
                                  sx={{ bgcolor: '#7C3AED', color: '#fff', fontWeight: 700, fontSize: '11px', height: 20, minWidth: 40 }} />
                            <Typography sx={{ fontSize: '12px', color: '#8a92a6', minWidth: 130 }}>
                              {new Date(rev.createdAt).toLocaleString('tr-TR')}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#1a2535', minWidth: 110 }}>
                              {rev.changedByName || '—'}
                            </Typography>
                            {snap?.totalAmount != null && (
                                <Typography sx={{ fontSize: '13px', fontWeight: 700, color: BRAND }}>
                                  {snap.totalAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {rev.currency}
                                </Typography>
                            )}
                            <StatusBadge status={rev.status} />
                            {rev.note && (
                                <Tooltip title={rev.note}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#8a92a6', cursor: 'default' }}>
                                    <InfoOutlined sx={{ fontSize: 14 }} />
                                    <Typography sx={{ fontSize: '11px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {rev.note}
                                    </Typography>
                                  </Box>
                                </Tooltip>
                            )}
                            <Box sx={{ ml: 'auto' }}>
                              <Button size="small" variant="outlined" onClick={() => { setSelectedRevision(rev); setRevModalOpen(true); }}
                                      sx={{ ...outlineBtn('#7C3AED'), fontSize: '11px', py: 0.25, px: 1 }}>
                                Detay
                              </Button>
                            </Box>
                          </Box>
                        </ListItem>
                    );
                  })}
                </List>
            )}
          </Paper>
        </Collapse>

        {/* ── Bilgi Kartları ── */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {/* Müşteri */}
          <Paper elevation={0} sx={{ p: 2.5, flex: 1, minWidth: 240, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 30, height: 30, bgcolor: '#eff6ff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Business sx={{ color: BRAND, fontSize: 15 }} />
              </Box>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#8a92a6', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Müşteri</Typography>
            </Box>
            <InfoRow label="Firma"    value={offer.customerName}    bold />
            <InfoRow label="Yetkili"  value={offer.customerContact} />
            <InfoRow label="Ülke"     value={offer.customerCountry} />
            <InfoRow label="E-posta"  value={offer.customerEmail}   />
            <InfoRow label="Telefon"  value={offer.customerPhone}   />
          </Paper>

          {/* Teklif */}
          <Paper elevation={0} sx={{ p: 2.5, flex: 1, minWidth: 240, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <Box sx={{ width: 30, height: 30, bgcolor: '#eff6ff', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Description sx={{ color: BRAND, fontSize: 15 }} />
              </Box>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#8a92a6', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Teklif</Typography>
            </Box>
            <InfoRow label="Teklif No"   value={offer.orderNo}    bold />
            <InfoRow label="Tarih"       value={new Date(offer.createdAt).toLocaleDateString('tr-TR')} />
            <InfoRow label="Para Birimi" value={offer.currency}   />
            <InfoRow label="Teslim"      value={offer.deliveryLocation} />
            <InfoRow label="Ön Ödeme"    value={`%${((offer.downPaymentRate || 0) * 100).toFixed(0)}`} />
            <InfoRow label="Vade"        value={offer.paymentDays ? `${offer.paymentDays} gün` : '—'} />
            <InfoRow label="Hazırlayan"  value={offer.createdByName} />
          </Paper>

          {/* Toplam Tutar */}
          <Paper elevation={0} sx={{
            p: 3, minWidth: 200, borderRadius: '10px',
            background: `linear-gradient(135deg, ${BRAND} 0%, #0f2652 100%)`,
            display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 0.5,
          }}>
            <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: '10px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Toplam Tutar
            </Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 700, color: '#fff', lineHeight: 1.1 }}>
              {grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: '13px' }}>{offer.currency}</Typography>
            <Box sx={{ mt: 1 }}><StatusBadge status={offer.status} size="md" /></Box>
          </Paper>
        </Box>

        {/* ── Teklif Notu ── */}
        {offer.notes && (
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #fde68a', bgcolor: '#fffbeb', borderRadius: '10px' }}>
              <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Teklif Notu</Typography>
              <Typography sx={{ fontSize: '13px', color: '#1a2535' }}>{offer.notes}</Typography>
            </Paper>
        )}

        {/* ── Ürün Tablosu ── */}
        <Paper elevation={0} sx={{ p: 3, border: `1px solid ${BORDER}`, borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,.05)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <Box sx={{ width: 4, height: 20, bgcolor: BRAND, borderRadius: 1 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: '#1a2535' }}>Ürün Listesi</Typography>
            <Chip label={`${offer.items?.length || 0} ürün`} size="small"
                  sx={{ bgcolor: BRAND, color: '#fff', fontWeight: 700, fontSize: '11px', height: 20 }} />
          </Box>
          <TableContainer sx={{ border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TH width={36}>#</TH>
                  <TH>Ürün Adı</TH>
                  <TH>Kablo Ailesi</TH>
                  <TH width={60}>Birim</TH>
                  <TH align="right">Miktar</TH>
                  <TH align="right">Birim Fiyat</TH>
                  <TH align="right">Kâr %</TH>
                  <TH align="right">Toplam</TH>
                </TableRow>
              </TableHead>
              <TableBody>
                {offer.items?.map((item, idx) => (
                    <TableRow key={item.id} sx={{
                      bgcolor: '#fff', transition: 'background .1s',
                      '&:hover td': { bgcolor: '#fafbfe' },
                      '& td': { borderBottom: `1px solid ${BORDER}` },
                    }}>
                      <TableCell style={{ color: '#8a92a6', fontSize: 12 }}>{idx + 1}</TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600, fontSize: '13px', color: BRAND }}>{item.productName}</Typography>
                      </TableCell>
                      <TableCell>
                        {item.cableFamily && (
                            <Chip label={item.cableFamily} size="small"
                                  sx={{ fontSize: '10px', height: 18, fontWeight: 700, borderRadius: '5px' }} />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip label={item.productUnit} size="small" variant="outlined" sx={{ fontSize: '10px', height: 18, borderRadius: '5px' }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: '12px' }}>{item.quantity?.toLocaleString('tr-TR')}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#16a34a' }}>
                          {currencySym}{item.unitPrice?.toLocaleString('tr-TR', { minimumFractionDigits: 3 })}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Box component="span" sx={{ display: 'inline-block', px: 1, py: '2px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, bgcolor: '#f0fdf4', color: '#16a34a' }}>
                          %{((item.profitMargin || 0) * 100).toFixed(1)}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: BRAND }}>
                          {currencySym}{item.totalPrice?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {offer.shippingAmount ? (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography sx={{ fontSize: '12px', color: '#8a92a6' }}>Ürünler Toplamı</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px' }}>{currencySym}{itemsTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</Typography>
                  <Typography sx={{ fontSize: '12px', color: '#8a92a6', mt: 0.5 }}>Nakliye</Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: '14px', color: '#b45309' }}>+ {currencySym}{offer.shippingAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</Typography>
                  <Divider sx={{ my: 1 }} />
                </Box>
              </Box>
          ) : <Divider sx={{ my: 2 }} />}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ px: 3, py: 2, bgcolor: BRAND, borderRadius: '10px', textAlign: 'right' }}>
              <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>Genel Toplam</Typography>
              <Typography sx={{ fontSize: 28, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                {grandTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {offer.currency}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* ── PDF Dialog ── */}
        <Dialog open={!!pdfUrl} onClose={handleClosePdf} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
          <DialogContent sx={{ p: 0, height: '90vh', position: 'relative' }}>
            <IconButton onClick={handleClosePdf}
                        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, bgcolor: '#fff', boxShadow: '0 2px 8px rgba(0,0,0,.15)', borderRadius: '8px' }}>
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
            {pdfUrl && <iframe src={pdfUrl} width="100%" height="100%" title="PDF Önizleme" style={{ border: 'none' }} />}
          </DialogContent>
        </Dialog>

        {/* ── Revizyon Detay Modalı ── */}
        <Dialog open={revModalOpen} onClose={() => setRevModalOpen(false)} maxWidth="md" fullWidth
                PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden' } }}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f5f0ff', borderBottom: '1px solid #e9d5ff', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <History sx={{ color: '#7C3AED', fontSize: 18 }} />
              <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
                {offer.orderNo} — {selectedRevision?.revisionLabel}
              </Typography>
              <Chip label={selectedRevision?.revisionLabel} size="small" sx={{ bgcolor: '#7C3AED', color: '#fff', fontWeight: 700, height: 20, fontSize: '11px' }} />
            </Box>
            <IconButton size="small" onClick={() => setRevModalOpen(false)}
                        sx={{ borderRadius: '7px', border: `1px solid ${BORDER}` }}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ pt: 3 }}>
            {selectedRevision && (() => {
              const snap = parseSnapshot(selectedRevision.snapshotJson);
              return (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
                      {[
                        { label: 'Arşivlenme', val: new Date(selectedRevision.createdAt).toLocaleString('tr-TR') },
                        { label: 'Değiştiren',  val: selectedRevision.changedByName || '—' },
                        { label: 'Para / Teslim', val: `${selectedRevision.currency} / ${selectedRevision.deliveryLocation}` },
                      ].map(({ label, val }) => (
                          <Box key={label} sx={{ flex: 1, minWidth: 160 }}>
                            <Typography sx={{ fontSize: '11px', color: '#8a92a6', mb: 0.25 }}>{label}</Typography>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{val}</Typography>
                          </Box>
                      ))}
                      <Box sx={{ flex: 1, minWidth: 100 }}>
                        <Typography sx={{ fontSize: '11px', color: '#8a92a6', mb: 0.5 }}>Durum</Typography>
                        <StatusBadge status={selectedRevision.status} />
                      </Box>
                    </Box>

                    {selectedRevision.note && (
                        <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px' }}>
                          <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 0.5 }}>Revizyon Notu</Typography>
                          <Typography sx={{ fontSize: '13px' }}>{selectedRevision.note}</Typography>
                        </Paper>
                    )}

                    {snap?.items && (
                        <>
                          <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#8a92a6', textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1 }}>
                            Bu Revizyondaki Ürün Listesi
                          </Typography>
                          <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: '8px', overflow: 'hidden' }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TH width={36}>#</TH>
                                  <TH>Ürün</TH>
                                  <TH align="right">Miktar</TH>
                                  <TH align="right">Birim Fiyat</TH>
                                  <TH align="right">Kâr %</TH>
                                  <TH align="right">Toplam</TH>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {snap.items.map((item: any, idx: number) => (
                                    <TableRow key={idx} sx={{ '& td': { borderBottom: `1px solid ${BORDER}` } }}>
                                      <TableCell style={{ color: '#8a92a6', fontSize: 12 }}>{idx + 1}</TableCell>
                                      <TableCell><Typography sx={{ fontWeight: 600, fontSize: '13px' }}>{item.productName}</Typography></TableCell>
                                      <TableCell align="right"><Typography sx={{ fontSize: '12px' }}>{item.quantity?.toLocaleString('tr-TR')}</Typography></TableCell>
                                      <TableCell align="right"><Typography sx={{ fontSize: '13px', fontWeight: 700 }}>{item.unitPrice?.toLocaleString('tr-TR', { minimumFractionDigits: 3 })} {selectedRevision.currency}</Typography></TableCell>
                                      <TableCell align="right"><Typography sx={{ fontSize: '12px', color: '#16a34a', fontWeight: 600 }}>{((item.profitMargin || 0) * 100).toFixed(1)}%</Typography></TableCell>
                                      <TableCell align="right"><Typography sx={{ fontWeight: 700, fontSize: '13px' }}>{item.totalPrice?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {selectedRevision.currency}</Typography></TableCell>
                                    </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#7C3AED', borderRadius: '8px', textAlign: 'right' }}>
                              <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.25 }}>Toplam</Typography>
                              <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                                {snap.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {selectedRevision.currency}
                              </Typography>
                            </Box>
                          </Box>
                        </>
                    )}
                  </Box>
              );
            })()}
          </DialogContent>
        </Dialog>
      </Box>
  );
};

export default OfferDetailPage;
